import { Box, Checkbox } from '@material-ui/core';
import { FilterOperator } from '@nemuru-eng/js-api-client';
import { useState } from 'react';

import { ListFilter } from '@/components/modules/CheckoutSession/List/types/ListFilter';
import { Font } from '@/components/shared/components/Font';

export interface BooleanFilterDefinition {
  id: string;
  label: JSX.Element;
  field: string;
  operator: FilterOperator;
  activeValue?: string;
  defaultActive?: boolean;
}

interface UseBooleanFiltersProps {
  definitions: BooleanFilterDefinition[];
  initialActiveIds?: string[];
}

export const useBooleanFilters = ({ definitions, initialActiveIds }: UseBooleanFiltersProps) => {
  const defaultActiveIds = definitions.filter((def) => def.defaultActive).map((def) => def.id);

  const computeInitialState = (): Record<string, boolean> => {
    const state: Record<string, boolean> = {};
    definitions.forEach((def) => {
      if (initialActiveIds) {
        state[def.id] = initialActiveIds.includes(def.id);
      } else {
        state[def.id] = !!def.defaultActive;
      }
    });
    return state;
  };

  const [activeState, setActiveState] = useState<Record<string, boolean>>(computeInitialState);

  if (!definitions.length) {
    return {
      booleanFilters: [] as ListFilter[],
      booleanFiltersContent: <></>,
      activeIds: [] as string[],
      isDefaultBooleanValue: true,
      nonDefaultBooleanFilterCount: 0,
      resetBooleanFiltersToDefaults: () => {},
    };
  }

  const toggle = (id: string) => {
    setActiveState((current) => ({ ...current, [id]: !current[id] }));
  };

  const resetBooleanFiltersToDefaults = () => {
    const next: Record<string, boolean> = {};
    definitions.forEach((def) => {
      next[def.id] = !!def.defaultActive;
    });
    setActiveState(next);
  };

  const activeIds = definitions.filter((def) => activeState[def.id]).map((def) => def.id);
  const isDefaultBooleanValue =
    activeIds.length === defaultActiveIds.length &&
    activeIds.every((id) => defaultActiveIds.includes(id)) &&
    defaultActiveIds.every((id) => activeIds.includes(id));

  const nonDefaultBooleanFilterCount = definitions.filter(
    (def) => !!activeState[def.id] !== !!def.defaultActive,
  ).length;

  const booleanFilters: ListFilter[] = definitions
    .filter((def) => activeState[def.id])
    .map((def) => ({
      field: def.field,
      operator: def.operator,
      ...(def.activeValue ? { value: [def.activeValue] } : {}),
    }));

  const booleanFiltersContent = (
    <Box>
      {definitions.map((def) => (
        <Box
          key={def.id}
          display="flex"
          alignItems="center"
          py={0}
          onClick={() => toggle(def.id)}
          style={{ cursor: 'pointer' }}
        >
          <Checkbox
            checked={!!activeState[def.id]}
            color="default"
            onClick={(event) => event.stopPropagation()}
            onChange={() => toggle(def.id)}
          />
          <Font variant="body2" color="textSecondary">
            {def.label}
          </Font>
        </Box>
      ))}
    </Box>
  );

  return {
    booleanFilters,
    booleanFiltersContent,
    activeIds,
    isDefaultBooleanValue,
    nonDefaultBooleanFilterCount,
    resetBooleanFiltersToDefaults,
  };
};
